import {
  Document,
  Page,
  View,
  Text,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import { Table, TD, TH, TR } from "@ag-media/react-pdf-table";
import { useTranslation } from "react-i18next";
import { Image } from "@react-pdf/renderer";
import { LogoEn } from "../../../../components/styled/AllImages";

Font.register({
  family: "TheSans-Bold",
  src: require("../../../../assets/fonts/arabic/TheSans-Bold.ttf"),
});
Font.register({
  family: "TheSans-Plain",
  src: require("../../../../assets/fonts/arabic/TheSans-Plain.ttf"),
});

const styles = StyleSheet.create({
  page: {
    backgroundColor: "#fff",
    color: "#262626",
    fontSize: "12px",
    padding: "50px",
  },
  header: {
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  heading: {
    textAlign: "center",
  },
  logoWrapper: {
    width: "200px",
    height: "40px",
    overflow: "hidden",
  },
  logo: {
    objectFit: "contain",
    height: "100%",
    width: "100%",
  },
  subLabel: {
    fontFamily: "TheSans-Plain",
    fontSize: "20px",
  },
  theSansPlain: {
    fontFamily: "TheSans-Plain",
    fontSize: "10px",
  },
  infoSection: {
    border: "1px solid #000",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
  },
  dateNameWrapper: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  date: {
    flexDirection: "row",
    gap: "52px",
    alignItems: "center",
    padding: "15px 5px",
    width: "100%",
  },
  dateTitle: {
    display: "flex",
    flexDirection: "column",
  },
  title: {
    fontSize: "30px",
  },
  titleNormal: {
    fontFamily: "TheSans-Bold",
    fontSize: "9px",
  },
  textBold: {
    fontFamily: "TheSans-Bold",
    color: "#525355",
  },
  headingNormal: {
    fontFamily: "TheSans-Bold",
    fontSize: "11px",
  },
  headingMedium: {
    fontFamily: "TheSans-Bold",
    fontSize: "10px",
  },
  headingSmall: {
    fontFamily: "TheSans-Bold",
    fontSize: "9px",
  },
  textNormal: {
    fontSize: "12px",
  },
  table: {
    width: "100%",
    borderColor: "1px solid #f3f4f6",
    margin: "20px 0",
  },
  tableHeader: {
    backgroundColor: "#e5e5e5",
  },
  td: {
    paddingVertical: 2,
    paddingHorizontal: 2,
    fontSize: "11px",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    border: "1px solid #000",
    breakInside: "avoid",
    flexGrow: 0.3,
  },
  tdDep: {
    paddingVertical: 2,
    paddingHorizontal: 2,
    fontSize: "11px",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    border: "1px solid #000",
    breakInside: "avoid",
    flexGrow: 0.7,
  },
  tdContact: {
    paddingVertical: 2,
    paddingHorizontal: 2,
    fontSize: "11px",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    border: "1px solid #000",
    breakInside: "avoid",
    flexGrow: 0.45,
  },
  tdId: {
    width: "5px",
    // padding: 6,
    fontSize: "11px",
    display: "flex",
    flexDirection: "column",
    border: "1px solid #000",
    breakInside: "avoid",
  },
  borderBot: {
    borderBottom: "1px solid #000",
    width: "100%",
  },
  name: {
    display: "flex",
    alignItems: "center",
    gap: "47px",
    flexDirection: "row",
    paddingTop: "0px",
    paddingBottom: "15px",
    paddingLeft: "5px",
    paddingRight: "5px",
    fontSize: "11px",
  },
  upperCase: {
    textTransform: "uppercase",
  },
  textWrap: {
    width: "90%",
    wordBreak: "normal",
    lineBreak: "auto",
  },
  dataTextWrap: {
    wordBreak: "break-word",
    lineBreak: "anywhere",
  },
  marginXDir: {
    marginRight: "120px",
  },
  wordWrap: {
    wordBreak: "break-word",
  },
  row: {
    breakInside: "avoid", // prevent rows from splitting
    pageBreakInside: "avoid", // cross-browser safety
    flexDirection: "row",
  },
});

export const PdfDocumentTemplate = ({
  user,
  departmentsList,
  departmentsHeadings,
  hqBranchesList,
  hqBranchesHeadings,
}) => {
  const getFormattedDateWithMonthName = (date) => {
    const options = { month: "short", day: "2-digit", year: "numeric" };
    return new Intl.DateTimeFormat("en-US", options).format(new Date(date));
  };

  const textWrap = (text) => {
    if (text?.length > 20) {
      return text
        .split("")
        .map((char, index) =>
          index % 20 === 0 && index !== 0 ? char + "\u200B" : char
        )
        .join("");
    }
    return text;
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.logoWrapper}>
            <Image style={styles.logo} src={LogoEn} />
          </View>
          <View style={styles.heading}>
            <Text style={[styles.title, styles.textBold]}>Cloud Archive</Text>
            <Text style={styles.subLabel}>أرشيف السحابة</Text>
          </View>
          <View style={styles.infoSection}>
            <View style={styles.dateNameWrapper}>
              <View style={[styles.date]}>
                <View style={styles.dateTitle}>
                  <Text style={[styles.textBold, styles.upperCase]}>Date</Text>
                  <Text style={styles.theSansPlain}>التاريخ</Text>
                </View>
                <Text
                  style={[styles.textBold, styles.textNormal, styles.textWrap]}
                >
                  {getFormattedDateWithMonthName(new Date())}
                </Text>
              </View>
              <View
                style={[styles.date, styles.marginXDir, { marginLeft: "14px" }]}
              >
                <View style={styles.dateTitle}>
                  <Text style={[styles.textBold, styles.upperCase]}>Email</Text>
                  <Text style={styles.theSansPlain}>إيميل</Text>
                </View>
                <Text style={[styles.textBold, styles.textNormal]}>
                  {user?.email}
                </Text>
              </View>
            </View>
            <View style={[styles.borderBot]}></View>
            <View style={styles.dateNameWrapper}>
              <View style={styles.name}>
                <View style={styles.dateTitle}>
                  <Text style={[styles.textBold, styles.upperCase]}>Name</Text>
                  <Text style={styles.theSansPlain}>الاسم</Text>
                </View>
                <Text
                  style={[styles.textBold, styles.textNormal, styles.textWrap]}
                >
                  {user?.name}
                </Text>
              </View>
              <View
                style={[
                  styles.date,
                  styles.marginXDir,
                  { marginBottom: "17px" },
                ]}
              >
                <View style={styles.dateTitle}>
                  <Text style={[styles.textBold, styles.upperCase]}>
                    Organization
                  </Text>
                  <Text style={styles.theSansPlain}>منظمة</Text>
                </View>
                <Text style={[styles.textBold, styles.textNormal]}>
                  {departmentsList &&
                  departmentsList?.length > 0 &&
                  departmentsList[0]?.location?.name_en
                    ? `${user?.current_organization?.organization_name} (${departmentsList[0]?.location?.name_en})`
                    : user?.current_organization?.organization_name}
                </Text>
              </View>
            </View>
          </View>
        </View>

        <Table style={[styles.table, styles.wordWrap]}>
          <TH style={[styles.tableHeader, styles.textBold]}>
            {departmentsHeadings
              ? departmentsHeadings?.map((item, i) => (
                  <TD
                    style={[
                      Number(item.index) === 1
                        ? styles.tdId
                        : Number(item.index) === 5
                        ? styles.tdContact
                        : styles.tdDep,
                    ]}
                    key={i}
                  >
                    <Text style={styles.headingNormal}>{item.nameEn}</Text>
                    <Text style={styles.headingSmall}>{item.nameAr}</Text>
                  </TD>
                ))
              : hqBranchesHeadings?.map((item, i) => (
                  <TD
                    style={[
                      [1, 5, 6].includes(item.index) ? styles.tdId : styles.td,
                    ]}
                    key={i}
                  >
                    <Text style={styles.headingNormal}>{item.nameEn}</Text>
                    <Text style={styles.headingSmall}>{item.nameAr}</Text>
                  </TD>
                ))}
          </TH>
          {departmentsList
            ? departmentsList?.map((item, i) => (
                <TR key={item?.id}>
                  <TD
                    break={true}
                    wrap={true}
                    style={[styles.tdId, styles.headingMedium, styles.wordWrap]}
                  >
                    {i + 1}
                  </TD>
                  <TD
                    break={true}
                    wrap={true}
                    style={[
                      styles.tdDep,
                      styles.headingMedium,
                      styles.wordWrap,
                    ]}
                  >
                    {item?.name_en}
                    {item?.name_ar}
                  </TD>
                  <TD
                    break={true}
                    wrap={true}
                    style={[
                      styles.tdDep,
                      styles.headingMedium,
                      styles.wordWrap,
                    ]}
                  >
                    {item?.city?.en_name}
                    {item?.city?.ar_name}
                  </TD>
                  <TD
                    break={true}
                    wrap={true}
                    style={[styles.tdDep, styles.headingMedium]}
                  >
                    {textWrap(item?.email)}
                  </TD>
                  <TD
                    break={true}
                    wrap={true}
                    style={[
                      styles.tdContact,
                      styles.headingMedium,
                      styles.wordWrap,
                    ]}
                  >
                    {item?.contact_no}
                  </TD>
                </TR>
              ))
            : hqBranchesList?.map((item, i) => (
                <TR key={item.id}>
                  <TD
                    break={true}
                    wrap={true}
                    style={[styles.tdId, styles.headingMedium]}
                  >
                    {i + 1}
                  </TD>
                  <TD
                    break={true}
                    wrap={true}
                    style={[styles.td, styles.headingMedium, styles.wordWrap]}
                  >
                    {item?.type_en}
                    {item?.type_ar}
                  </TD>
                  <TD
                    break={true}
                    wrap={true}
                    style={[styles.td, styles.headingMedium, styles.wordWrap]}
                  >
                    {item?.name_en}
                    {item?.name_ar}
                  </TD>
                  <TD
                    break={true}
                    wrap={true}
                    style={[styles.td, styles.headingMedium, styles.wordWrap]}
                  >
                    {item?.city?.en_name}
                    {item?.city?.ar_name}
                  </TD>
                  <TD
                    break={true}
                    wrap={true}
                    style={[styles.tdId, styles.headingMedium]}
                  >
                    {item?.no_of_departments}
                  </TD>
                  <TD
                    break={true}
                    wrap={true}
                    style={[styles.tdId, styles.headingMedium]}
                  >
                    {item?.no_of_users}
                  </TD>
                </TR>
              ))}
        </Table>
      </Page>
    </Document>
  );
};
